import PlaceIcon from '@mui/icons-material/PlaceOutlined';
import { Box, Divider, Paper } from '@mui/material';
import { MapPopup } from '@sofarocean/atlas/maps';
import { isEqual } from 'lodash';

import { IconData } from '@/components/data/IconData';
import { LatLong } from '@/components/data/LatLong';
import { SpotterInspector } from '@/components/map/inspectors/SpotterInspector';
import { WeatherInspector } from '@/components/map/inspectors/WeatherInspector';
import { useMapUiContext } from '@/context/MapUiContext';

type InspectorPopupProps = {
    onClose?: () => void;
    showCoordinateSeconds?: boolean;
    historicalData?: boolean;
};

export const InspectorPopup = ({
    onClose = () => {},
    showCoordinateSeconds = false,
    historicalData,
}: InspectorPopupProps) => {
    const { mapInspector, setMapInspector, weatherModel } = useMapUiContext();

    if (!mapInspector || (mapInspector.type === 'weather' && !weatherModel)) {
        return false;
    }

    if (historicalData && (mapInspector.type !== 'spotter' || mapInspector.spotterFamily !== 'mine')) {
        // no historical weather or other people's spotters yet
        return false;
    }

    return (
        <MapPopup
            focusAfterOpen={false}
            position={mapInspector.position}
            zIndex={1000}
            onClose={() => {
                setMapInspector((currentValue) => {
                    if (isEqual(currentValue, mapInspector)) {
                        onClose?.();
                        // only unset the inspector if the current inspector is the one we were showing
                        return undefined;
                    }
                    return currentValue;
                });
            }}
            sx={{
                p: 0,
                background: 'none',
                boxShadow: 'initial',
            }}
        >
            <Paper
                sx={{
                    borderRadius: 1,
                }}
            >
                <IconData Icon={PlaceIcon} sx={{ p: 1 }}>
                    <LatLong coord={mapInspector.position} options={{ showSecs: showCoordinateSeconds }} />
                </IconData>
                {mapInspector.type === 'spotter' && (
                    <>
                        <Divider sx={{ borderColor: 'inherit', opacity: 0.75 }} />
                        <Box sx={{ p: 1 }}>
                            <SpotterInspector {...mapInspector} historicalData={historicalData} />
                        </Box>
                    </>
                )}
                {weatherModel && (
                    <>
                        <Divider sx={{ borderColor: 'inherit', opacity: 0.75 }} />
                        <Box sx={{ p: 1 }}>
                            <WeatherInspector
                                position={mapInspector.position}
                                allVariables={mapInspector.type === 'weather'}
                            />
                        </Box>
                    </>
                )}
            </Paper>
        </MapPopup>
    );
};
